import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { apiClient } from 'modules/utils/httpClient'
import { getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import type { SeedTracker } from 'modules/domain/seedTracker/types'
import HistoryDisplay, { defaultFieldFormatter } from 'views/components/SeedTracker/HistoryDisplay/HistoryDisplay'
import { FullWidthCard, FullWidthFlex } from '../styles'

type HistoryEntry = {
  created_at: string
  author_name: string
  operation: string
  action: string
  collection: string
  diff: Array<{ field_name: string; old_value: string; new_value: string }>
}

// Field name mapping for localization
const FIELD_NAME_MAP: Record<string, string> = {
  product_card_id: 'productCard',
  origin_company_id: 'originCompany',
  destination_company_id: 'destinationCompany',
  order_status: 'orderStatus',
  sales_order_number: 'salesOrderNumber',
  purchase_order_number: 'purchaseOrderNumber',
  sales_invoice_number: 'salesInvoiceNumber',
  current_pedigree: 'currentPedigree',
  sold_as_pedigree: 'soldAsPedigree',
  quantity_mt: 'quantity',
  origin_current_mt: 'originCurrent',
  origin_requested_mt: 'originRequested',
  origin_after_sale_mt: 'originAfterSale',
  destination_current_mt: 'destinationCurrent',
  destination_requested_mt: 'destinationRequested',
  destination_after_sale_mt: 'destinationAfterSale',
}

/** Label for seed-tracker row = product card (variety + crop + class). */
function seedTrackerVarietyLabel(row: SeedTracker): string {
  const parts = [row.variety, row.seed_crop_name, row.seed_class_name].filter(
    (p) => p != null && String(p).trim() !== '',
  )
  return parts.length > 0 ? parts.join(' · ') : row.id
}

const SeedOrderHistoryTab: React.FC = () => {
  const { t } = useTranslation('seedTracker')
  const { id } = useParams<{ id: string }>()
  const [entries, setEntries] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [varietyMap, setVarietyMap] = useState<Record<string, string>>({})

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setEntries([])
        return
      }
      const cancelled = false
      setLoading(true)
      try {
        // Fetch history and seed trackers in parallel
        const [historyData, trackerData] = await Promise.all([
          apiClient.get<HistoryEntry[]>(`/seed_orders/${id}/history`),
          getSeedTrackersList({}, {}, 1, 5000),
        ])

        if (!cancelled) {
          setEntries(historyData ?? [])
          // Build variety mapping
          const varietyByProductCardId = Object.fromEntries(
            (trackerData.data ?? []).map((r) => [r.id, seedTrackerVarietyLabel(r)]),
          )
          setVarietyMap(varietyByProductCardId)
        }
      } catch (error) {
        if (!cancelled) {
          setEntries([])
          setVarietyMap({})
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadData()
  }, [id])

  // Custom formatter that wraps defaultFieldFormatter but intercepts product_card_id
  const customFormatter = (fieldName: string, value: string, isOldValue: boolean) => {
    // Handle product_card_id specially: look up variety from varietyMap
    if (fieldName === 'product_card_id' && value) {
      const variety = varietyMap[value] || value
      // Now use the default formatter with the variety string instead of the ID
      return defaultFieldFormatter(fieldName, variety, isOldValue)
    }
    // For all other fields, use the default formatter
    return defaultFieldFormatter(fieldName, value, isOldValue)
  }

  return (
    <FullWidthFlex vertical>
      <FullWidthCard title={t('detail.tabs.history', 'History')} variant="borderless">
        <HistoryDisplay
          entries={entries}
          loading={loading}
          empty={entries.length === 0}
          fieldNameMap={FIELD_NAME_MAP}
          fieldFormatter={customFormatter}
        />
      </FullWidthCard>
    </FullWidthFlex>
  )
}

export default SeedOrderHistoryTab
